// src/components/BarcodeScanner.jsx
import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader, NotFoundException } from "@zxing/library";
import { X } from "lucide-react";

const cx = (...c) => c.filter(Boolean).join(" ");

export default function BarcodeScanner({ onClose, onDetected, title = "Escanear código de barras" }) {
    const videoRef = useRef(null);
    const codeReaderRef = useRef(null);
    const [devices, setDevices] = useState([]);
    const [deviceId, setDeviceId] = useState(null);
    const [lastCode, setLastCode] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        let mounted = true;
        const init = async () => {
            try {
                // pedir permiso primero para obtener labels de cámaras
                await navigator.mediaDevices.getUserMedia({ video: true });
            } catch (_) {
                // si falla permiso, igual intentamos enumerar
            }

            try {
                codeReaderRef.current = new BrowserMultiFormatReader();

                const all = (await navigator.mediaDevices.enumerateDevices())
                    .filter(d => d.kind === "videoinput");

                if (!mounted) return;
                setDevices(all);

                // elegir la trasera si se puede
                const preferred =
                    all.find(d => /back|trás|rear|environment/i.test(d.label))?.deviceId ||
                    all[0]?.deviceId ||
                    null;

                setDeviceId(preferred);
            } catch (e) {
                setError("No pudimos acceder a la cámara. Revisá permisos del navegador.");
            }
        };

        init();
        return () => {
            mounted = false;
            stopScanner();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!deviceId || !videoRef.current || !codeReaderRef.current) return;
        startScanner(deviceId);
        return () => stopScanner();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceId]);

    async function startScanner(devId) {
        setError("");
        try {
            await codeReaderRef.current.decodeFromVideoDevice(
                devId, // puede ser null para "default"
                videoRef.current,
                (result, err) => {
                    if (result) {
                        const text = String(result.getText() || "").trim();
                        if (text && text !== lastCode) {
                            setLastCode(text);
                            // leve “debounce” visual
                            setTimeout(() => onDetected?.(text), 50);
                        }
                    } else if (err && !(err instanceof NotFoundException)) {
                        // otros errores por frame: ignorar
                    }
                }
            );
        } catch (e) {
            setError("No pudimos iniciar el escaneo. Probá con otra cámara o cerrá otras apps que usen la cámara.");
        }
    }

    function stopScanner() {
        try { codeReaderRef.current?.reset(); } catch { }
        try {
            const el = videoRef.current;
            const stream = el?.srcObject;
            if (stream && stream.getTracks) stream.getTracks().forEach(t => t.stop());
        } catch { }
    }

    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[96vw] max-w-2xl bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden">
                <div className="p-3 border-b border-slate-200 flex items-center justify-between">
                    <p className="font-semibold">{title}</p>
                    <button onClick={onClose} className="h-9 w-9 grid place-items-center rounded-xl bg-white border border-slate-300 hover:bg-slate-50">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-3 grid gap-3">
                    {devices.length > 1 && (
                        <select
                            className="h-10 px-3 rounded-xl bg-white border border-slate-300"
                            value={deviceId || ""}
                            onChange={e => setDeviceId(e.target.value || null)}
                        >
                            {devices.map(d => (
                                <option key={d.deviceId} value={d.deviceId}>
                                    {d.label || `Cámara ${d.deviceId.slice(0, 6)}`}
                                </option>
                            ))}
                        </select>
                    )}

                    <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-black">
                        <video ref={videoRef} autoPlay muted playsInline style={{ width: "100%", height: "auto" }} />
                        {/* guía visual */}
                        <div className="absolute inset-0 pointer-events-none grid place-items-center">
                            <div className="w-[70%] max-w-md h-32 border-2 border-emerald-400/80 rounded-xl" />
                        </div>
                    </div>

                    {lastCode && (
                        <div className="text-sm text-slate-600">
                            Último código: <span className="font-mono">{lastCode}</span>
                        </div>
                    )}
                    {error && <div className="text-sm text-rose-600">{error}</div>}

                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => { stopScanner(); startScanner(deviceId); }}
                            className="h-10 px-3 rounded-xl border border-slate-300 bg-white hover:bg-slate-50"
                        >
                            Reintentar
                        </button>
                        <button
                            onClick={onClose}
                            className="h-10 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white"
                        >
                            Listo
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
